import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Group, Modal, Stack, Text } from "@mantine/core";
import { ExternalLink, MessageSquare, Unlink } from "lucide-react";
import { toast } from "sonner";
import { BooleanIcon } from "@nodus/ui";
import { tenantApi } from "@/lib/api/client";
import { SLACK_WORKSPACES_KEY, useSlackWorkspaces, type SlackWorkspace } from "@/hooks/use-slack-workspaces";

/** Connect the company's Slack workspace (or open / disconnect it). Used on
 *  Settings and in the Company setup guide (#436). Connecting leaves the page
 *  for Slack's consent screen; the guide re-checks the step when the member
 *  comes back. */
export function SlackInstallCard() {
  const queryClient = useQueryClient();
  const [unlinkTarget, setUnlinkTarget] = useState<SlackWorkspace | null>(null);
  const { data: workspaces = [], isLoading } = useSlackWorkspaces();

  const unlinkMutation = useMutation({
    mutationFn: () => tenantApi.unlinkSlackBot(),
    onSuccess: () => {
      toast.success("Slack workspace disconnected");
      queryClient.invalidateQueries({ queryKey: SLACK_WORKSPACES_KEY });
      queryClient.invalidateQueries({ queryKey: ["slack-channels"] });
      queryClient.invalidateQueries({ queryKey: ["comms-targets"] });
      setUnlinkTarget(null);
    },
    onError: () => toast.error("Failed to disconnect workspace"),
  });

  return (
    <>
      <Card withBorder padding="lg">
        <Group gap="sm" mb="md">
          <MessageSquare size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
          <div>
            <Text fw={500}>Add Nodus to Slack</Text>
            <Text fz="xs" c="dimmed">Install the AI assistant in your workspace</Text>
          </div>
        </Group>
        {isLoading ? (
          <Text fz="sm" c="dimmed">Loading...</Text>
        ) : workspaces.length > 0 ? (
          <Stack gap="sm">
            {workspaces.map((ws) => (
              <Group key={ws.team_id || ws.app_id} justify="space-between">
                <Group gap="xs" fz="sm">
                  <BooleanIcon value={true} />
                  <Text fw={700} component="span">{ws.team_name || "Workspace"}</Text>
                </Group>
                <Group gap={4}>
                  <Button variant="subtle" size="compact-sm" component="a" href={`https://slack.com/app_redirect?app=${ws.app_id}&team=${ws.team_id}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={12} style={{ marginRight: 4 }} />
                    Open
                  </Button>
                  <Button variant="subtle" size="compact-sm" disabled={unlinkMutation.isPending} onClick={() => setUnlinkTarget(ws)}>
                    <Unlink size={12} />
                  </Button>
                </Group>
              </Group>
            ))}
          </Stack>
        ) : (
          <Stack gap="md">
            <Text fz="sm" c="dimmed">
              No Slack workspace connected. Connect to get notifications and the AI assistant in your workspace.
            </Text>
            <Button fullWidth component="a" href="/slack/install">
              <MessageSquare size={16} style={{ marginRight: 8 }} />
              Connect to Slack now
            </Button>
          </Stack>
        )}
      </Card>

      <Modal
        opened={!!unlinkTarget}
        onClose={() => setUnlinkTarget(null)}
        title="Disconnect Slack workspace?"
      >
        <Text fz="sm" c="dimmed" mb="lg">
          This will remove the connection to <strong>{unlinkTarget?.team_name || "this workspace"}</strong>.
          Notifications will stop and any configured channels will be cleared.
          You can reconnect at any time.
        </Text>
        <Group justify="flex-end">
          <Button variant="outline" onClick={() => setUnlinkTarget(null)}>Cancel</Button>
          <Button onClick={() => unlinkMutation.mutate()}>
            Disconnect
          </Button>
        </Group>
      </Modal>
    </>
  );
}

export default SlackInstallCard;
